import { useState, useCallback } from 'react';
import { useAppStore, recalculateStore } from '@/store/useAppStore';
import { exportSession as exportSessionJSON, importSession as importSessionJSON, clearLocalStorage } from '@/store/persistence';
import { useToast } from '@/hooks/useToast';
import type { Course, AppSettings } from '@gpa-scales/shared-types';

export function SessionManager() {
  const [confirmClear, setConfirmClear] = useState(false);
  const courses = useAppStore((s) => s.courses);
  const settings = useAppStore((s) => s.settings);
  const { showToast } = useToast();

  const handleExport = useCallback(() => {
    const json = exportSessionJSON(courses, settings);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gpa-scales-session-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [courses, settings]);

  const handleImport = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const data = importSessionJSON(reader.result as string);
        if (data) {
          useAppStore.getState().importCourses(data.courses);
          useAppStore.getState().loadSettings(data.settings);
          recalculateStore();
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }, []);

  // R27 — Undo-aware clear
  const handleClear = useCallback(async () => {
    // Snapshot state for undo
    const snapshotCourses: Course[] = [...useAppStore.getState().courses];
    const snapshotSettings: AppSettings = { ...useAppStore.getState().settings };

    useAppStore.getState().clearAllCourses();
    useAppStore.getState().resetSettings();
    useAppStore.getState().resetContext();
    await clearLocalStorage();
    recalculateStore();
    setConfirmClear(false);

    showToast(
      'Session cleared.',
      'Undo',
      () => {
        useAppStore.getState().importCourses(snapshotCourses);
        useAppStore.getState().loadSettings(snapshotSettings);
        recalculateStore();
      },
    );
  }, [showToast]);

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <h3 className="mb-3 text-sm font-semibold text-[var(--color-text)]">Session Manager</h3>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleExport}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
        >
          Export JSON
        </button>
        <button
          onClick={handleImport}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
        >
          Import JSON
        </button>

        {confirmClear ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-error">Clear all courses and settings? This can&rsquo;t be undone.</span>
            <button
              onClick={handleClear}
              className="rounded-md bg-error px-3 py-1.5 text-xs font-medium text-white"
            >
              Clear Everything
            </button>
            <button
              onClick={() => setConfirmClear(false)}
              className="rounded-md border border-border px-3 py-1.5 text-xs text-[var(--color-text-muted)]"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmClear(true)}
            className="rounded-md border border-error/40 px-3 py-1.5 text-xs font-medium text-error hover:bg-error-light"
          >
            Clear All Data
          </button>
        )}
      </div>
    </div>
  );
}
